import {
  observable,
  action,
  computed,
  autorun,
} from 'mobx';
import moment from 'moment-timezone';
import omit from 'lodash/omit';
import { ActionEnum } from '../constants/ActionEum';
import logger from '../utils/logger';
import { WebsocketEvent } from '../constants/WebsocketEvent';
import { displayDate } from '../utils/date';
import shortid from 'shortid';
import startCase from 'lodash/startCase';
import { AccountType } from '../constants/ItemType';
import { abortController } from '../utils/abort-request';
import {
  playAudio,
  Sounds,
} from '../components/Sound';
import { BatchTicketStatus } from '../constants/BatchTicketStatus';
import { OrderOrigin } from '../constants/OrderOrigin';
import { toJs } from '../utils/toJs';

class ScheduleStore {
  @observable
    loading = false;
  @observable
    schedules = [];
  @observable
    customers = [];
  @observable
    projects = [];
  @observable
    units = [];
  @observable
    accountType = Object.keys(AccountType).map(v => { return { value: AccountType[v], text: startCase(v) };});
  @observable
    orderId = null;
  @observable
    actionMode = '';
  @observable
    localOrders = [];
  @observable
    offlineOrders = [];
  @observable
    dateFrom = null;
  @observable
    dateTo = null;
  @observable
    trucks = [];
  @observable
    drivers = [];
  @observable
    isBatched = false;
  @observable
    orderOptions = [];
  @observable
    mixDesignForDispatchTickets = {};
  @observable
    isPreSaveOrder = false;
  @observable
    preSavedOrders = {};
  @observable
    areDatesInitialized = false;

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.setColors();

    autorun(() => {
      if (!this.rootStore.settingsStore) {
        logger.error('Settings store is not initialized. Make sure it is initialized before ScheduleStore');
        return;
      }
      if (!this.rootStore.settingsStore.configuration) {
        return;
      }
      // Wait for the configuration to be loaded
      this.initializeDates();
    });
  }

  get api() {
    return this.rootStore.newAuthStore;
  }

  get plantId() {
    return this.rootStore.locationsStore.plantId;
  }

  @computed
  get timezone() {
    return this.rootStore.settingsStore.configuration?.ianaTimeZone || null;
  }

  @computed
  get offlineOrdersNameSpace() {
    return 'offlineOrders';
  }

  getScheduleById = id => this.schedules.find(s => s.id === id);

  getLocalOrderById = id => this.localOrders.find(s => s.id === id);

  @action
  initializeDates() {
    this.dateFrom = moment.tz(this.timezone).startOf('day');
    this.dateTo = moment.tz(this.timezone).endOf('day');
    this.areDatesInitialized = true;
  }

  setColors() {
    clearInterval(this.colorInterval);
    this.colorInterval = setInterval(() => {
      this.schedules = this.setColorLevel(this.schedules);
      this.localOrders = this.setColorLevel(this.localOrders);
    }, 10 * 1000);
  }

  setColorLevel(orders) {
    let level = 0;
    const now = moment().tz(this.timezone);

    return orders.map((v) => {
      const loadTime = moment(v.date).tz(this.timezone);
      const diff = loadTime.diff(now, 'second');

      if (diff < 0) { // < 0 seconds
        level = 0;
      } else if (diff < 60 * 2.5) { // < 2.5 minutes
        level = 1;
      } else if (diff < 60 * 5) { // < 5 minutes
        level = 2;
      } else if (diff < 60 * 7.5) { // < 7.5 minutes
        level = 3;
      } else if (diff < 60 * 10) { // < 10 minutes
        level = 4;
      } else if (diff > 60 * 10) { // > 10 minutes
        level = 5;
      }

      return {...v, colorLevel: level};
    });

  }

  get isPreview() { return this.actionMode === ActionEnum.PREVIEW; };
  get isEdit() { return this.actionMode === ActionEnum.EDIT; };
  get isAdd() { return this.actionMode === ActionEnum.ADD; };

  preSaveOrder(payload) {
    this.preSavedOrders[payload.id] = payload;
  }

  deletePreSavedOrder(id) {
    delete this.preSavedOrders[id];
  }

  getPreSavedOrder(id) {
    return this.preSavedOrders[id];
  }

  getOrderOptions() {
    const { mixStore } = this.rootStore;
    const orders = [].concat(this.schedules).concat(this.localOrders).filter(v => v.code);

    const orderOptions =  mixStore.getOptions(orders, {
      key: 'id',
      text: 'code',
      value: 'code',
    });

    return orderOptions;
  }

  @action
  addOrderOption(data) {
    if (!this.orderOptions.find(v => v.value === data.value)) {
      this.orderOptions.push({id: shortid(), value: data.value, text: data.value});
    }
  }

  @action
    onClose = () => {
      this.cancelEdit();
    };

  @action
    newOrder = () => {
      this.cancelEdit();
      this.actionMode = ActionEnum.ADD;
    };

  @action
    editOrder = orderId => {
      this.actionMode = ActionEnum.EDIT;
      this.orderId = orderId;
    };

  @action
    previewOrder = orderId => {
      this.actionMode = ActionEnum.PREVIEW;
      this.orderId = orderId;
    };

  @action
    cancelEdit = () => {
      this.orderId = null;
      this.actionMode = null;
    };

  hideOfflineTicketsInScheduler() {
    const { batchStore, ticketStore } = this.rootStore;
    const offlineOrdersIds = this.offlineOrders.map(v => v.id);
    const offlineTicketsIds = ticketStore.offlineTicketsFromDispatch.map(v => v.id);
    const currentLoadsIds = batchStore.running.map(v => v.id);
    this.schedules = this.schedules.filter(v => ![...offlineTicketsIds, ...offlineOrdersIds, ...currentLoadsIds].includes(v.id));
    logger.log('hide offline tickets in scheduler:', offlineTicketsIds, offlineOrdersIds, currentLoadsIds, this.schedules.map(v => v.id));
  }

  handleScheduleResponse = (resp) => {
    this.loading = false;
    this.orderId = null;
    let data = resp || [];
    data = this.setColorLevel(data);
    this.schedules = data.filter(v => (v.origin === OrderOrigin.Dispatch && v.batchTicketStatus === BatchTicketStatus.None)
      || (v.origin === OrderOrigin.External && v.batchTicketStatus !== BatchTicketStatus.Started && v.batchTicketStatus !== BatchTicketStatus.Created));

    this.rootStore.ticketStore.loadOfflineTicketsFromDispatch();

    this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineOrdersNameSpace))
      .then(offlineData => {
        this.offlineOrders = offlineData || [];
        this.localOrders = this.offlineOrders.filter(v => v.batchTicketStatus !== BatchTicketStatus.Batched)
          .concat(data.filter(v => v.origin === OrderOrigin.Local && v.orderMixQuantity > v.orderDeliveredMixQuantity));
        this.orderOptions = this.getOrderOptions();
        this.hideOfflineTicketsInScheduler();
      });

    this.loadMixDesignForDispatchTickets();
  };

  loadMixDesignForDispatchTickets(reload = false) {
    this.schedules.forEach(v => {
      if (reload || !this.mixDesignForDispatchTickets[v.id]) {
        this.loadMixDesignForDispatchTicket(v.id, v.origin === OrderOrigin.External);
      }
    });
  }

  @action
  loadMixDesignForDispatchTicket(id, isExternal = false) {
    const url = isExternal
      ? `/api/plant/${this.plantId}/externalOrder/${id}/mixDesign`
      : `/api/ticket/${id}/mixDesign`;

    return this.api
      .makeAuthorizedRequest(url)
      .then(resp => {
        if (isExternal) {
          const x = this.mapExternalMixDesignForTicket(resp);
          if (x.constituentItems.length) {
            this.mixDesignForDispatchTickets[id] = x;
          }
        } else {
          this.mixDesignForDispatchTickets[id] = resp;
        }
        return resp;
      })
      .catch(() => {
        logger.warn(`Couldn't load mix design for ticket ${id}`);
        if (isExternal) {
          this.fetchMixDesignForExternalOrderWithNoMixDesign(id);
        }
      });
  }

  @action
  fetchMixDesignForExternalOrderWithNoMixDesign(id) {
    const order = this.schedules.find(v => v.id === id);
    const mix = order ? order.products.find(v => v.isMix) : null;
    logger.log('fetch mix design for external order with no mix design', id, order, mix);

    if (order && order.origin === OrderOrigin.External && mix && mix.itemId) {
      const mixDesign = this.rootStore.mixStore.getMixDesign(mix.itemId);

      if (mixDesign) {
        this.mixDesignForDispatchTickets[id] = {
          ...mix,
          constituentItems: [ ...mixDesign ],
        };
        logger.log('mix design for external order #', id, this.mixDesignForDispatchTickets[id]);
      } else {
        logger.warn(`no mix design for external order #${id}`, mix.itemId);
      }
    }
  }

  @action
  mapExternalMixDesignForTicket(externalMixDesign) {
    const { notifierStore, materialStore } = this.rootStore;
    const { materialsWithDetails } = materialStore;
    let constituentItems = [];

    externalMixDesign.constituentItems.forEach(v => {
      const m = materialsWithDetails.find(m => m.itemCode === v.itemCode);

      if (m) {
        constituentItems.push({
          batchCode: m.batchCode,
          batchDescription: m.batchDescription,
          constituentItemId: m.itemId,
          constituentItemCode: m.itemCode,
          constituentItemShortDescription: m.itemShortDescription,
          materialType: v.type,
          quantity: v.quantity,
          unitId: m.itemBatchUnitId,
          unitSymbol: m.itemBatchUnitSymbol,
        });
      } else {
        constituentItems.push({
          constituentItemId: v.itemCode,
          constituentItemCode: v.itemCode,
          constituentItemShortDescription: v.itemCode,
          quantity: v.quantity,
        });

        notifierStore.showWarning({ key: 'Missing material', props: { materialCode: v.itemCode }});
      }
    });

    return {
      ...externalMixDesign,
      constituentItems,
    };
  }

  @action
  loadSchedules(abortTime = 0) {
    if (!this.rootStore.connectionStore.isOnline) {
      this.handleScheduleResponse(this.schedules);
      return Promise.resolve(this.schedules);
    }

    if (!this.areDatesInitialized) {
      const errorMessage = 'Can\'t fetch the schedule. Dates have not been initialized yet.';
      logger.error(errorMessage);
      return Promise.reject(errorMessage);
    }

    this.loading = true;
    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/schedule`,
        {
          'date.from': this.dateFrom.format(),
          'date.to': this.dateTo.format(),
          skip: 0,
          take: 100,
          isBatched: this.isBatched,
        },
        'GET',
        abortController(abortTime)
      )
      .then(resp => {
        this.handleScheduleResponse(resp);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action
  isEmptyMixDesign(order = {}) {
    const mix = order.products && order.products.length
      ? order.products.find(v => v.isMix)
      : null;
    let mixDesign = mix
      ? this.rootStore.mixStore.getMixDesign(Number(mix.itemId))
      : [];

    if (!mixDesign.length) {
      const m = this.mixDesignForDispatchTickets[order.id];
      mixDesign = m?.constituentItems?.length ? m.constituentItems : [];
    }

    return !mixDesign.length;
  }

  updateOfflineOrder(data, isEdit = false) {
    data = toJs({ ...data, scales: [], pumps: [], logo: '', isOffline: true });

    if (data.origin === OrderOrigin.External) {
      this.offlineOrders.push(data);
    } else {
      const orderIndex = this.offlineOrders.findIndex(v => v.id === data.id);
      if (orderIndex > -1) {
        const tickets = this.offlineOrders[orderIndex].tickets;

        if (Array.isArray(tickets)) {
          if (isEdit) {
            this.offlineOrders[orderIndex] = data;
          } else {
            this.offlineOrders[orderIndex] = {
              ...this.offlineOrders[orderIndex],
              orderDeliveredMixQuantity: data.orderDeliveredMixQuantity,
              batchTicketStatus: data.batchTicketStatus,
              tickets: [...tickets, ...data.tickets],
            };
          }
        } else {
          this.offlineOrders[orderIndex] = data;
        }
      } else {
        this.offlineOrders.push(data);
      }
    }

    this.api.storeDB.init()
      .then(() => this.api.storeDB.save(this.offlineOrdersNameSpace, toJs(this.offlineOrders)));

    this.loadSchedules();
  }

  removeOfflineTicket(id) {
    return this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineOrdersNameSpace))
      .then(offlineOrders => {
        offlineOrders.forEach(order => {
          order.tickets = order.tickets.filter(v => v.id !== id);
        });

        return this.api.storeDB.init()
          .then(() => this.api.storeDB.save(this.offlineOrdersNameSpace, toJs(offlineOrders)));
      });

  }

  @action
  syncOfflineOrders() {
    const { connectionStore } = this.rootStore;
    const { isOnline } = connectionStore;

    if (!isOnline) {
      return;
    }

    this.api.storeDB.init()
      .then(() => this.api.storeDB.getByKey(this.offlineOrdersNameSpace))
      .then(data => {
        let offlineOrders = data || [];

        offlineOrders = offlineOrders.map(v => Object.assign({}, isNaN(v.id) ? omit(v, ['id']) : v));
        offlineOrders.forEach((order, i) => {
          if (order.origin === OrderOrigin.External) {
            offlineOrders[i].ticket = omit(order.ticket, ['id']);
          } else {
            offlineOrders[i].tickets = offlineOrders[i].tickets
              ? order.tickets.map(v => Object.assign({}, isNaN(v.id) ? omit(v, ['id']) : v))
              : [];
          }
        });

        offlineOrders = offlineOrders.filter(v => !v.dispatchOrderId);

        if (!offlineOrders.length) {
          this.api.storeDB.init().then(() => this.api.storeDB.save(this.offlineOrdersNameSpace, []));

          return;
        }

        const externalOrders = offlineOrders.filter(v => v.origin === OrderOrigin.External);
        const localOrders = offlineOrders.filter(v => v.origin === OrderOrigin.Local);

        if (externalOrders.length) {
          this.syncOfflineExternalOrders(externalOrders);
        }

        if (localOrders.length) {
          this.syncOfflineLocalOrders(localOrders);
        }
      });

  }

  syncOfflineLocalOrders(offlineOrders) {
    const { ticketStore, notifierStore } = this.rootStore;
    this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/localOrder/sync`,
        offlineOrders,
        'POST'
      )
      .then(() => {
        this.offlineOrders = this.offlineOrders.filter(v => v.origin !== OrderOrigin.Local);
        this.api.storeDB.init().then(() => this.api.storeDB.save(this.offlineOrdersNameSpace, toJs(this.offlineOrders)));
        ticketStore.loadTickets();
        notifierStore.showInfo('Offline local tickets saved!');
      })
      .catch(e => notifierStore.showWarning({ key: 'Can\'t sync offline data!', props: { error: e.message }}));

  }

  syncOfflineExternalOrders(offlineOrders) {
    const { ticketStore, notifierStore } = this.rootStore;
    this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/externalOrder/sync`,
        offlineOrders,
        'POST'
      )
      .then(() => {
        this.offlineOrders = this.offlineOrders.filter(v => v.origin !== OrderOrigin.External);
        this.api.storeDB.init().then(() => this.api.storeDB.save(this.offlineOrdersNameSpace, toJs(this.offlineOrders)));
        ticketStore.loadTickets();
        notifierStore.showInfo('Offline external tickets saved!');
      })
      .catch(e => notifierStore.showWarning({ key: 'Can\'t sync offline data!', props: { error: e.message }}));

  }

  @action
  addLocalOrder(data) {
    const { isOnline } = this.rootStore.connectionStore;

    if (!isOnline) {
      this.updateOfflineOrder(data);
      return Promise.resolve();
    }

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/localOrder`,
        Object.assign({}, omit(data, ['id'])),
        'POST'
      )
      .then(() => {
        this.loadSchedules();
      });
  };

  @action
  updateLocalOrder(id, data) {
    const { isOnline } = this.rootStore.connectionStore;

    if (id !== this.orderId) {
      this.orderId = null;
    }

    if (!isOnline) {
      this.updateOfflineOrder({ ...data, origin: OrderOrigin.Local }, true);
      return Promise.resolve();
    }

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/localOrder/${id}`,
        data,
        'PUT'
      )
      .then(() => {
        this.loadSchedules();
      });
  };

  @action
  deleteLocalOrder(id) {
    this.orderId = null;

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/localOrder/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        return this.loadSchedules();
      });
  };

  loadOffline(key, url) {
    if (!this[key].length) {
      this.api.storeDB.init()
        .then(() => this.api.storeDB.getByKey(url))
        .then(data => {
          if (data && data.length && this[key] && !this[key].length) {
            this[key] = data || [];
            if (key === 'customers') {
              this.addNoCustomerItem();
            }
          }
        });
    }
  }

  @action
  deleteDispatchOrder(id, reasonId) {
    this.orderId = null;

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/ticket/${id}?reasonId=${reasonId}`,
        null,
        'DELETE'
      )
      .then(() => {
        return this.loadSchedules();
      });
  };

  @action
  deleteExternalOrder(id) {
    this.orderId = null;

    return this.api
      .makeAuthorizedRequest(
        `/api/plant/${this.plantId}/externalOrder/${id}`,
        null,
        'DELETE'
      )
      .then(() => {
        return this.loadSchedules();
      });
  };

  addNoCustomerItem() {
    if (!this.customers.find(v => v.id === -2)) {
      this.customers.unshift({
        id: -2,
        name: '',
        code: 'No customer',
      });
    }
  }

  @action
    loadCustomers = () => {
      const url = '/api/customer';
      this.loadOffline('customers', url);

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.customers = resp || [];
          this.addNoCustomerItem();
        });
    };

  @action
    loadProjects = () => {
      const url = '/api/project';
      this.loadOffline('projects', url);

      return this.api
        .makeAuthorizedRequest(url)
        .then(resp => {
          this.projects = resp || [];
        });
    };

  @action
    loadTrucks = () => {
      const url = '/api/truck';
      this.loadOffline('trucks', url);

      return this.api
        .makeAuthorizedRequest(url, {
          sorting: 'code',
        })
        .then(resp => {
          this.trucks = resp || [];
        });
    };

  @action
    loadDrivers = () => {
      const url = '/api/employee';
      this.loadOffline('drivers', url);

      return this.api
        .makeAuthorizedRequest(url, {
          position: 'Driver',
          sorting: 'code',
        })
        .then(resp => {
          this.drivers = resp || [];
        });
    };

  startWebSockets = () => {
    const { notifierStore, websocketStore, connectionStore, settingsStore, sidebarStore, batchStore, newAuthStore } = this.rootStore;
    this.ws = websocketStore.connection;
    clearTimeout(this.wsTimer);

    if (!this.ws || !connectionStore.isOnline || !websocketStore.isTrulyConnected) {
      this.wsTimer = setTimeout(() => {
        this.startWebSockets();
      }, 2000);
      return;
    }

    this.ws.on(WebsocketEvent.TOKEN_EXPIRATION_ALERT, () => {
      logger.log('ws:', WebsocketEvent.TOKEN_EXPIRATION_ALERT);
      this.api.refresh().finally(() => this.ws.stop());
    });

    this.ws.on(WebsocketEvent.JOB_ADD, order => {
      logger.log('ws:', WebsocketEvent.JOB_ADD, order);
      if (!order || order.plantId !== this.plantId || !order.id || this.localOrders.find(v => v.id === order.id) || this.schedules.find(v => v.id === order.id)) {
        return;
      }

      const { configuration } = settingsStore;
      const { user } = newAuthStore;

      if (order.origin === OrderOrigin.Local) {
        this.localOrders.push(order);

        if (settingsStore.notificationSettings.order) {
          notifierStore.showInfo({
            key: 'Local order added!',
            props: { orderCode: order.code, date: displayDate(order.date, this.timezone, 'L LT', user.language) },
          });
        }
      } else {
        this.schedules.push(order);
        const isExternal = order.origin === OrderOrigin.External;
        this.loadMixDesignForDispatchTicket(order.id, isExternal).then(() => {
          if (configuration.enableQuickDispatchTicket && !sidebarStore.type) {
            batchStore.setOrderId(null);

            setTimeout(() => {
              batchStore.setOrderId(order.id);
              sidebarStore.onToggle('batchForm');
            }, 0);

          }
        });

        if (settingsStore.notificationSettings.order) {
          notifierStore.showInfo({
            key: isExternal ? 'External order added!' : 'Ticket from dispatch added!',
            props: { orderCode: order.code, date: displayDate(order.date, this.timezone, 'L LT', user.language) },
          });
        }

        if (batchStore.canEmit) {
          batchStore.injectCode(batchStore.executeCode);
          batchStore.context.onTicketArrival(order, order.origin);
        }

      }

      if (configuration.audibleOnTicketArrival) {
        playAudio(Sounds.bip1);
      }

      this.orderOptions = this.getOrderOptions();
    });

    this.ws.on(WebsocketEvent.JOB_UPDATE, order => {
      logger.log('ws:', WebsocketEvent.JOB_UPDATE, order);
      if (order && order.plantId === this.plantId) {
        let list = order.origin === OrderOrigin.Local ? this.localOrders : this.schedules;
        const index = list.findIndex(v => v.id === order.id);
        if (index > -1) {
          list[index] = order;
        }
      }
    });

    this.ws.on(WebsocketEvent.JOB_BATCH, order => {
      logger.log('ws:', WebsocketEvent.JOB_BATCH, order);
      if (order && order.plantId === this.plantId) {
        let list = order.origin === OrderOrigin.Local ? this.localOrders : this.schedules;
        const index = list.findIndex(v => v.id === order.id);

        if (index > -1) {
          list[index] = Object.assign(list[index], order);
        }
      }
    });

    this.ws.on(WebsocketEvent.JOB_DELETE, order => {
      logger.log('ws:', WebsocketEvent.JOB_DELETE, order);
      if (order.id === this.orderId) {
        this.orderId = null;
      }
      if (order.origin === OrderOrigin.Local) {
        this.localOrders = this.localOrders.filter(v => v.id !== order.id);
      } else {
        this.schedules = this.schedules.filter(v => v.id !== order.id);
      }
    });

    this.ws.on(WebsocketEvent.CUSTOMER_ADD, resp => {
      logger.log('ws:', WebsocketEvent.CUSTOMER_ADD, resp);

      if (resp) {
        this.loadCustomers();
      }
    });

    this.ws.on(WebsocketEvent.CUSTOMER_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.CUSTOMER_UPDATE, resp);

      if (resp) {
        this.loadCustomers();
      }
    });

    this.ws.on(WebsocketEvent.PROJECT_ADD, resp => {
      logger.log('ws:', WebsocketEvent.PROJECT_ADD, resp);

      if (resp) {
        this.loadProjects();
        if (settingsStore.notificationSettings.project) {
          notifierStore.showInfo('Project added!');
        }
      }
    });

    this.ws.on(WebsocketEvent.PROJECT_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.PROJECT_UPDATE, resp);

      if (resp) {
        this.loadProjects();
        if (settingsStore.notificationSettings.project) {
          notifierStore.showInfo('Project updated!');
        }
      }
    });

    this.ws.on(WebsocketEvent.PROJECT_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.PROJECT_DELETE, resp);

      if (resp) {
        this.projects = this.projects.filter(v => v.id !== resp.id);
        if (settingsStore.notificationSettings.project) {
          notifierStore.showInfo('Project deleted!');
        }
      }
    });

    this.ws.on(WebsocketEvent.EMPLOYEE_ADD, resp => {
      logger.log('ws:', WebsocketEvent.EMPLOYEE_ADD, resp);

      if (resp && resp.isDriver && resp.plantId === this.plantId) {
        this.loadDrivers();
      }
    });

    this.ws.on(WebsocketEvent.EMPLOYEE_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.EMPLOYEE_UPDATE, resp);

      if (resp && resp.isDriver && resp.plantId === this.plantId) {
        this.loadDrivers();
      }
    });

    this.ws.on(WebsocketEvent.EMPLOYEE_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.EMPLOYEE_DELETE, resp);

      if (resp) {
        const driver = this.drivers.find(v => v.id === resp.id);
        if (driver) {
          this.drivers = this.drivers.filter(v => v.id !== resp.id);
        }
      }
    });

    this.ws.on(WebsocketEvent.TRUCK_ADD, resp => {
      logger.log('ws:', WebsocketEvent.TRUCK_ADD, resp);

      if (resp && resp.plantId === this.plantId) {
        this.loadTrucks();
        if (settingsStore.notificationSettings.truck) {
          notifierStore.showInfo('Truck added!');
        }
      }
    });

    this.ws.on(WebsocketEvent.TRUCK_UPDATE, resp => {
      logger.log('ws:', WebsocketEvent.TRUCK_UPDATE, resp);

      if (resp) {
        this.loadTrucks();
        if (settingsStore.notificationSettings.truck) {
          notifierStore.showInfo('Truck updated!');
        }
      }
    });

    this.ws.on(WebsocketEvent.TRUCK_DELETE, resp => {
      logger.log('ws:', WebsocketEvent.TRUCK_DELETE, resp);

      if (resp) {
        this.trucks = this.trucks.filter(v => v.id !== resp.id);
        if (settingsStore.notificationSettings.truck) {
          notifierStore.showInfo('Truck deleted!');
        }
      }
    });

    this.ws.on(WebsocketEvent.ORDER_ADJUSTED_MIX_ADD, order => {
      logger.log('ws:', WebsocketEvent.ORDER_ADJUSTED_MIX_ADD, order);
      this.loadMixDesignForDispatchTickets(true);
    });

    this.ws.on(WebsocketEvent.ORDER_ADJUSTED_MIX_UPDATE, order => {
      logger.log('ws:', WebsocketEvent.ORDER_ADJUSTED_MIX_UPDATE, order);
      const o = this.schedules.find(v => v.dispatchOrderId === order.orderId);
      if (o) {
        this.loadMixDesignForDispatchTicket(o.id);
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo(`Mix Design for ticket ${o.dispatchTicketCode} updated!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.ORDER_ADJUSTED_MIX_DELETE, order => {
      logger.log('ws:', WebsocketEvent.ORDER_ADJUSTED_MIX_DELETE, order);
      const o = this.schedules.find(v => v.dispatchOrderId === order.orderId);
      if (o) {
        this.loadMixDesignForDispatchTicket(o.id);
        if (settingsStore.notificationSettings.mix) {
          notifierStore.showInfo(`Mix Design for ticket ${o.dispatchTicketCode} updated!`);
        }
      }
    });

    this.ws.on(WebsocketEvent.BATCH_TICKET_ADD, (data) => {
      logger.log('ws:', WebsocketEvent.BATCH_TICKET_ADD, data);

      if (this.orderId && (this.orderId === data.batchOrderId || this.orderId === data.dispatchTicketId)) {
        this.cancelEdit();
      }

      if (data.origin === OrderOrigin.External && data.batchOrderId) {
        this.schedules = this.schedules.filter(v => v.id !== data.batchOrderId);
      }

      if (data.origin === OrderOrigin.Dispatch && data.dispatchTicketId) {
        this.schedules = this.schedules.filter(v => v.id !== data.dispatchTicketId);
      }

      this.loadSchedules();
    });

    this.ws.onclose(() => {
      this.startWebSockets();
    });
  };
}

export default ScheduleStore;
